'use client';

import { useMemo, useRef } from 'react';

/**
 * Picks the active "design stage" to narrate above the render progress bar
 * from the current render progress (0..100).
 *
 * The stages advance with progress (honest — never runs ahead of the render),
 * but each stage gets a RANDOMIZED dwell weight so some linger longer than
 * others: the line feels like live, uneven work rather than an even pendulum.
 * Weights are seeded once per mount, so every render paces differently.
 *
 * Mirrors Apple `RenderProgressContentProvider.designStage` and Android
 * `rememberDesignStage`.
 */
export function useDesignStage(
  stages: string[] | undefined,
  progress: number,
  isActive: boolean
): string | null {
  // Seed once per mount. A ref (not state) keeps the weights stable across
  // the many progress re-renders without re-seeding.
  const seedRef = useRef<number[] | null>(null);

  // Keyed on the count only (not array identity): a fresh fetch returns a new
  // array reference every time but the same stage list — we must NOT reseed the
  // random weights on that, or the pacing would jump mid-render.
  const count = stages?.length ?? 0;

  const thresholds = useMemo(() => {
    if (count === 0) {
      seedRef.current = null;
      return [] as number[];
    }
    if (!seedRef.current || seedRef.current.length !== count) {
      // Weight in [0.85, 1.15): a small ±15% jitter around an even dwell so the
      // pacing feels alive but never lets one stage linger while another flashes.
      seedRef.current = Array.from({ length: count }, () => 0.85 + Math.random() * 0.3);
    }
    const weights = seedRef.current;
    const cumulative: number[] = [];
    let sum = 0;
    for (const w of weights) {
      sum += w;
      cumulative.push(sum);
    }
    // Normalize to (0, 1] — each entry is the upper progress-fraction bound
    // of its stage's band.
    return cumulative.map((c) => c / sum);
  }, [count]);

  if (!isActive || !stages || stages.length === 0) return null;

  const fraction = Math.min(Math.max(progress / 100, 0), 1);
  let index = thresholds.findIndex((t) => t > fraction);
  if (index < 0) index = stages.length - 1;
  return stages[index] ?? null;
}
